import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Card, Heading, PageHead, Text } from '../ui/index.js';
import { useAccess, useMarkNotificationsRead, useNotifications } from '../api/hooks.js';
import { Focusable, FocusZone } from '../components/focus/Focusable.js';
import { EmptyState, Loading } from '../components/layout/Loading.js';
import { formatRelative } from '../lib/format.js';
import { NOTIFICATION_TYPE_LABELS } from '../lib/notificationLabels.js';

export const Route = createFileRoute('/notifications')({ component: Notifications });

/** The feed. Opening it marks everything read, which clears the badge in the navigation. */
function Notifications() {
  const navigate = useNavigate();
  const { signedIn } = useAccess();
  const { data, isLoading } = useNotifications();
  const markRead = useMarkNotificationsRead();
  const marked = React.useRef(false);

  React.useEffect(() => {
    if (marked.current || !data || (data.unreadCount ?? 0) === 0) return;
    marked.current = true;
    markRead.mutate();
  }, [data, markRead]);

  if (!signedIn) {
    return <EmptyState title="Sign in to continue." />;
  }
  if (isLoading) return <Loading />;

  const items = data?.items ?? [];
  if (items.length === 0) return <EmptyState title="Nothing yet." />;

  return (
    <FocusZone focusKey="notifications" direction="column" className="gb-stack">
      <PageHead eyebrow="What happened" title="Notifications." />

      {items.map((item, index) => (
        <Focusable
          key={item.id}
          autoFocus={index === 0}
          onSelect={() => {
            if (item.link) void navigate({ to: item.link });
          }}
          selectLabel={item.title}
        >
          <Card size="2">
            <Text size="1" color="gray">
              {NOTIFICATION_TYPE_LABELS[item.type] ?? item.type} · {formatRelative(item.createdAt)}
            </Text>
            <Heading size="3">{item.title}</Heading>
            <Text render={<p />} size="2" color="gray">
              {item.body}
            </Text>
          </Card>
        </Focusable>
      ))}
    </FocusZone>
  );
}
